import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { PLANS, PACKS } from '../constants/models';
import * as api from '../services/api';
import Icons from '../components/Icons';
import toast from 'react-hot-toast';
import { initializePaddle } from '@paddle/paddle-js';

// Payment method rules per plan
const PLAN_PAY_METHODS = {
  basic: ['bkash'],     // Basic: bKash (Card temporarily disabled)
  go:    ['bkash'],     // Go: bKash
  plus:  ['bkash'],     // Plus: bKash
};

const BD_CARD_FIXED_FEE_USD = 0.50;
const BD_CARD_PERCENT_FEE = 0.075;

function getBdCardUsdPrice(basePrice) {
  const price = Number(basePrice) || 0;
  return Math.ceil((price + BD_CARD_FIXED_FEE_USD) / (1 - BD_CARD_PERCENT_FEE));
}

const INTL_PLAN_OVERRIDES = {
  basic: { priceUSD: 10, credits: 1660 },
  go: { priceUSD: 25, credits: 4700 },
  plus: { priceUSD: 50, credits: 10000 },
};

const INTL_PACKS = [
  { id: 'intl_2800', credits: 2800, priceUSD: 15, popular: true },
  { id: 'intl_3750', credits: 3750, priceUSD: 20 },
];

const LEMON_PENDING_MEMO_KEY = 'aifield_lemon_pending_memo';
const LEMON_PENDING_MEMO_TTL_MS = 30 * 60 * 1000;

function readPendingLemonMemo() {
  try {
    const raw = localStorage.getItem(LEMON_PENDING_MEMO_KEY);
    if (!raw) return null;
    if (raw.trim().startsWith('{')) {
      const parsed = JSON.parse(raw);
      return parsed?.memoId ? parsed : null;
    }
    return { memoId: raw, createdAt: 0 };
  } catch {
    return null;
  }
}

function savePendingLemonMemo(memoId) {
  localStorage.setItem(LEMON_PENDING_MEMO_KEY, JSON.stringify({
    memoId,
    createdAt: Date.now(),
  }));
}

function clearStalePendingLemonMemo() {
  const pending = readPendingLemonMemo();
  if (pending && (!pending.createdAt || Date.now() - pending.createdAt > LEMON_PENDING_MEMO_TTL_MS)) {
    localStorage.removeItem(LEMON_PENDING_MEMO_KEY);
    return null;
  }
  return pending;
}

export default function SubscriptionPage({ onShowAuth }) {
  const { token } = useAuth();
  const { credits, setCredits, plan, setPlan, refreshProfile, refresh, refreshPayHistory } = useCredits();

  const [selPlan, setSelPlan] = useState('go');
  const [selPack, setSelPack] = useState(3);
  const [billing, setBilling] = useState('monthly');
  const [packPayMethod, setPackPayMethod] = useState('bkash');
  const [subLoading, setSubLoading] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showPlanPayPicker, setShowPlanPayPicker] = useState(null); // plan id or null
  const paddleRef = useRef(null);

  // --- bKash Specific States ---
  const [payContext, setPayContext] = useState(null); // { type: 'plan' | 'pack', id, amount, credits }
  const [bkashTab, setBkashTab] = useState('pay'); // 'pay' | 'check'
  const [senderNum, setSenderNum] = useState('');
  const [trxId, setTrxId] = useState('');
  const [checkTrx, setCheckTrx] = useState('');
  const [bkashStatus, setBkashStatus] = useState(null); // { type: 'pending'|'error'|'success', msg: '' }
  const [checkStatusRes, setCheckStatusRes] = useState(null);
  const [countdown, setCountdown] = useState(0); 
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const planOrder = ['basic', 'go', 'plus'];
  const currentPlanIdx = planOrder.indexOf(plan);
  const isBD = Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Dhaka';
  const market = isBD ? 'bd' : 'intl';
  const displayPlans = isBD
    ? PLANS
    : PLANS.map(planItem => {
      const override = INTL_PLAN_OVERRIDES[planItem.id];
      if (!override) return planItem;
      return {
        ...planItem,
        priceUSD: override.priceUSD,
        credits: override.credits,
        features: planItem.features.map((feature, index) => (
          index === 0 ? `${override.credits.toLocaleString()} credits/mo` : feature
        )),
      };
    });
  const displayPacks = isBD
    ? PACKS.map((pack, index) => ({ ...pack, id: String(index), sourceIndex: index }))
    : INTL_PACKS;
  const selectedPack = displayPacks[selPack] || displayPacks[0];
  const addonExpiryNotice = isBD
    ? 'অ্যাড-অন ক্রেডিট আপনার বর্তমান ক্রেডিট সাইকেল শেষ হলে মেয়াদ শেষ হবে। আপনার প্ল্যান যদি ১ দিন পরে রিনিউ হয়, এই ক্রেডিটও ১ দিন থাকবে। yearly প্ল্যানে পরের মাসের ক্রেডিট রিফ্রেশ পর্যন্ত থাকবে।'
    : 'Add-on credits expire at your current credit cycle end. If your plan renews in 1 day, the add-on lasts 1 day. On yearly plans, add-ons last until the next monthly credit refresh.';

  useEffect(() => {
    if (!isBD) setPackPayMethod('card');
  }, [isBD]);

  useEffect(() => {
    if (selPack >= displayPacks.length) setSelPack(0);
  }, [selPack, displayPacks.length]);

  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const setPlanRef = useRef(setPlan);
  useEffect(() => { setPlanRef.current = setPlan; }, [setPlan]);

  const refreshProfileRef = useRef(refreshProfile);
  useEffect(() => { refreshProfileRef.current = refreshProfile; }, [refreshProfile]);

  const setCreditsRef = useRef(setCredits);
  useEffect(() => { setCreditsRef.current = setCredits; }, [setCredits]);

  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    initializePaddle({
      environment: 'production',
      token: 'live_7a2309ddf7e495aa76fb45f9ecc',
      eventCallback: async (event) => {
        if (event.name === 'checkout.completed') {
          const { transaction_id, custom_data } = event.data;
          const memoId = custom_data?.memoId;
          const planId = custom_data?.plan;
          if (!memoId || !planId) {
            toast.error('Payment data missing. Contact support.');
            return;
          }
          try {
            const currentToken = tokenRef.current;
            if (!currentToken) {
              toast.error('Session expired. Please log in again.');
              return;
            }
            const verifyRes = await api.verifyPaddlePayment(currentToken, {
              memoId,
              transactionId: transaction_id || `paddle_${Date.now()}`
            });
            if (verifyRes.success) {
              setCreditsRef.current(verifyRes.credits);
              setPlanRef.current(planId);
              const planName = PLANS.find(p => p.id === planId)?.name || planId;
              const planCredits = PLANS.find(p => p.id === planId)?.credits || 0;
              toast.success(`🎉 Upgraded to ${planName}! +${planCredits} credits added.`, { duration: 5000 });
              // Force-reload credits + plan from server (this is the key call)
              await refreshRef.current(true);
              setShowPayModal(false);
              setShowPlanPayPicker(null);
              paddleRef.current?.Checkout?.close();
            } else {
              toast.error('Payment was received but verification had an issue. Credits may still be applied. Please refresh.');
            }
          } catch (verifyErr) {
            const errMsg = verifyErr.response?.data?.error;
            const errStatus = verifyErr.response?.status;
            
            // 409 = already processed (double-fire), just refresh without error
            if (errStatus === 409) {
              await refreshRef.current(true);
              toast.success('Payment already applied! Credits refreshed.');
              paddleRef.current?.Checkout?.close();
            } else {
              console.error('[Paddle] Verify error:', errMsg, errStatus);
              toast.error(errMsg || 'Payment verification failed. Please contact support if credits are missing.');
            }
          } finally {
            setSubLoading(false);
          }
        } else if (event.name === 'checkout.closed') {
          setSubLoading(false);
        }
      }
    }).then(paddle => { paddleRef.current = paddle; }).catch(() => {});
  }, []); // Only initialize once — token/refresh read via refs

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isLemonReturn = params.get('payment') === 'lemonsqueezy';
    const pendingMemo = clearStalePendingLemonMemo();
    if (!isLemonReturn) return;
    if (!token) return;

    const memoId = params.get('memo') || pendingMemo?.memoId;
    params.delete('payment');
    params.delete('memo');
    const cleanQuery = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}${window.location.hash}`);

    if (!memoId) {
      toast('Payment received. Credits will refresh after confirmation.', { duration: 5000 });
      refreshRef.current(true);
      return;
    }

    let cancelled = false;
    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    (async () => {
      toast.loading('Finalizing card payment...', { id: 'lemonsqueezy-return' });
      for (let attempt = 0; attempt < 12 && !cancelled; attempt++) {
        try {
          const res = await api.checkLemonSqueezyPayment(token, memoId);
          if (res.status === 'completed') {
            setCreditsRef.current(res.credits || 0);
            if (res.plan) setPlanRef.current(res.plan);
            await refreshRef.current(true);
            refreshPayHistory(true);
            localStorage.removeItem(LEMON_PENDING_MEMO_KEY);
            toast.success(res.type === 'pack' ? 'Card payment completed. Credits added!' : 'Card payment completed. Plan is active!', { id: 'lemonsqueezy-return', duration: 5000 });
            return;
          }
          if (res.status === 'failed' || res.status === 'expired') {
            localStorage.removeItem(LEMON_PENDING_MEMO_KEY);
            toast.error(`Card payment ${res.status}. Please try again.`, { id: 'lemonsqueezy-return' });
            return;
          }
        } catch (e) {
          if (e.response?.status === 404 || e.response?.status === 410) {
            localStorage.removeItem(LEMON_PENDING_MEMO_KEY);
            toast.error('Card payment expired. Please try again.', { id: 'lemonsqueezy-return' });
            return;
          }
          // Keep polling briefly; the webhook can arrive a moment after redirect.
        }
        await wait(2500);
      }
      if (!cancelled) {
        await refreshRef.current(true);
        refreshPayHistory(true);
        toast('Payment received. Credits will refresh once Lemon Squeezy confirms it.', { id: 'lemonsqueezy-return', duration: 6000 });
      }
    })();

    return () => { cancelled = true; };
  }, [token, refreshPayHistory]);

  // --- Polling & Countdown ---
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((c) => {
            if (c === 1) return 0;
            return c - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  // Smart Long-Polling: Auto-refreshes the page via server events, ZERO database query spam!
  let waitActive = useRef(false);
  const startStatusWait = async (trx) => {
      waitActive.current = true;
      while (waitActive.current) {
          try {
              const res = await api.waitBkashStatus(token, trx);
              if (!waitActive.current) break;
              
              if (res.status === 'completed' || res.status === 'verified') {
                  setBkashStatus({ type: 'success', msg: '<strong>Payment verified! ✓</strong><br>Your credits have been added. This window will close shortly.' });
                  refreshProfile();
                  refresh(true);
                  refreshPayHistory(true); // update payment history cache without extra DB call
                  waitActive.current = false;
                  // Auto-close the modal after 2.5 seconds so user can read the message
                  setTimeout(() => setShowPayModal(false), 2500);
                  break;
              } else if (res.status === 'timeout') {
                  // The server 50s timeout hit without any updates. Reconnect instantly to keep watching.
                  continue;
              } else {
                  // Unknown status or hard rejected
                  break; 
              }
          } catch (e) {
              // On network drop, wait 5 seconds and retry to avoid browser UI freezes
              await new Promise(r => setTimeout(r, 5000));
          }
      }
  };

  useEffect(() => {
      return () => {
          waitActive.current = false;
      }
  }, []);


  const openPayModal = (type, identifier) => {
      if (!isBD) {
          toast.error('bKash is only available for Bangladesh pricing.');
          return;
      }
      if (!token) {
          if (onShowAuth) onShowAuth();
          else toast.error('Please log in to continue.');
          return;
      }
      let amount = 0, cred = 0;
      if (type === 'plan') {
          const p = displayPlans.find(x => x.id === identifier);
          if (!p) return;
          amount = billing === 'yearly' ? Math.round(p.priceBDT * 12 * 0.85) : p.priceBDT;
          cred = p.credits;
      } else {
          const p = displayPacks[identifier];
          if (!p) return;
          amount = p.priceBDT;
          cred = p.credits;
      }
      setPayContext({ type, identifier, amount, credits: cred });
      // Reset states
      setBkashTab('pay');
      setSenderNum('');
      setTrxId('');
      setCheckTrx('');
      setBkashStatus(null);
      setCheckStatusRes(null);
      setCountdown(0);
      waitActive.current = false;
      setShowPayModal(true);
  }

  const startLemonCheckout = async (payload, loadingSetter = setSubLoading) => {
    loadingSetter(true);
    setShowPlanPayPicker(null);
    const checkoutWindow = window.open('about:blank', '_blank');
    if (!checkoutWindow) {
      toast.error('Please allow pop-ups to open card checkout.');
      loadingSetter(false);
      return;
    }
    try {
      const checkout = await api.createLemonSqueezyCheckout(token, payload);
      if (!checkout.checkoutUrl) throw new Error('Missing Lemon Squeezy checkout URL');
      if (checkout.memoId) savePendingLemonMemo(checkout.memoId);
      checkoutWindow.opener = null;
      checkoutWindow.location.href = checkout.checkoutUrl;
      loadingSetter(false);
    } catch (e) {
      checkoutWindow.close();
      toast.error(e.response?.data?.error || 'Card checkout failed. Try again.');
      loadingSetter(false);
    }
  };

  // Handle card checkout via Lemon Squeezy.
  const handleCardCheckout = async (planId) => {
    if (!token) {
      if (onShowAuth) onShowAuth();
      else toast.error('Please log in to continue.');
      return;
    }

    await startLemonCheckout({ type: 'plan', market, plan: planId, billing });
  };

  const handlePackCardCheckout = async () => {
    toast.error('Card payments are temporarily disabled.');
  };

  // Handle plan subscribe button click
  const handlePlanAction = (planId) => {
    if (!token) {
        if (onShowAuth) onShowAuth();
        else toast.error('Please log in to continue.');
        return;
    }
    if (!isBD) {
      toast.error('Card payments are temporarily disabled.');
      return;
    }

    const methods = PLAN_PAY_METHODS[planId];
    if (!methods) return;

    if (methods.length === 1 && methods[0] === 'bkash') {
      // Basic: go straight to bKash modal
      openPayModal('plan', planId);
    } else {
      // Go/Plus: show payment method picker
      setShowPlanPayPicker(planId);
    }
  };

  const submitBkash = async () => {
      if (!senderNum || !trxId) {
          setBkashStatus({ type: 'error', msg: 'Please fill in all bKash fields.'});
          return;
      }
      // Sender bKash number must be exactly 11 digits
      if (!/^\d{11}$/.test(senderNum.trim())) {
          setBkashStatus({ type: 'error', msg: 'Your bKash number must be exactly 11 digits (e.g. 01712345678).'});
          return;
      }
      // TrxID must be at least 6 alphanumeric characters
      if (trxId.trim().length < 6) {
          setBkashStatus({ type: 'error', msg: 'Transaction ID looks too short. Please copy it exactly from your bKash SMS.'});
          return;
      }

      setSubLoading(true);
      try {
          await api.submitBkashPayment(token, {
              senderNumber: senderNum,
              trxId: trxId.toUpperCase(),
              amount: payContext.amount,
              type: payContext.type,
              identifier: payContext.identifier,
              billing: billing
          });
          
          setBkashStatus({ type: 'pending', msg: `<strong>Submitted — under review.</strong><br>Your TrxID <code>${trxId.toUpperCase()}</code> has been received. Credits will be added once verified by admin. Use the <strong>Check status</strong> tab to see updates.` });
          setCountdown(600); // 10 mins visual countdown
          startStatusWait(trxId.toUpperCase());
      } catch (e) {
          setBkashStatus({ type: 'error', msg: e.response?.data?.error || 'Failed to submit payment.' });
      } finally {
          setSubLoading(false);
      }
  };

  const handleCheckStatus = async () => {
      if (!checkTrx) return;
      setCheckStatusRes({ type: 'pending', msg: 'Checking...' });
      try {
          const res = await api.checkBkashStatus(token, checkTrx.toUpperCase());
          if (res.status === 'completed' || res.status === 'verified') {
              setCheckStatusRes({ type: 'success', msg: 'Payment verified! Credits added. Closing...' });
              refreshProfile();
              refresh(true);
              refreshPayHistory(true);
              setTimeout(() => setShowPayModal(false), 2500);
          } else if (res.status === 'pending' || res.status === 'processing') {
              setCheckStatusRes({ type: 'pending', msg: 'Payment is still pending review.' });
          } else {
              setCheckStatusRes({ type: 'error', msg: `Payment status: ${res.status}` });
          }
      } catch (e) {
          setCheckStatusRes({ type: 'error', msg: e.response?.data?.error || 'Transaction not found or error.' });
      }
  };

  const handleReportIssue = async () => {
      try {
          await api.reportBkashIssue(token, trxId.toUpperCase());
          toast.success('Issue reported successfully. Our admin will check it.');
          setCountdown(-1); // hide button
      } catch (e) {
          toast.error('Failed to report issue');
      }
  };

  // Helper: get payment method icons for a plan
  const getPayMethodBadge = (planId) => {
    const methods = isBD ? PLAN_PAY_METHODS[planId] : [];
    if (!methods || methods.length === 0) return null;
    return (
      <div className="plan-pay-methods">
        {methods.includes('bkash') && <span className="ppm-badge bkash">🇧🇩 bKash</span>}
        {methods.includes('card') && <span className="ppm-badge card">💳 Card</span>}
      </div>
    );
  };

  return (
    <>
      <style>{`
          /* ── bKash Modal ── */
          .bk-card { background: var(--s1); border-radius: 12px; overflow: hidden; text-align: left; }
          .bk-header { background: #E2136E; padding: 1.25rem 1.5rem; display: flex; align-items: center; gap: 12px; }
          .bk-logo { width: 36px; height: 36px; background: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 500; font-size: 13px; color: #E2136E; }
          .bk-header-text h3 { color: white; margin: 0; font-size: 15px; font-weight: 500; }
          .bk-header-text p { color: rgba(255,255,255,0.8); margin: 0; font-size: 12px; }
          .bk-body { padding: 1.25rem; }
          .step-box { background: var(--bg); border-radius: 8px; padding: 1rem; margin-bottom: 1rem; border-left: 3px solid #E2136E; font-size: 13px; color: var(--t2); line-height: 1.5; }
          .merchant-num { font-size: 20px; font-weight: 500; color: var(--t1); letter-spacing: 1px; font-family: monospace; }
          .bk-label { display: block; font-size: 13px; color: var(--t2); margin-bottom: 6px; }
          .bk-input { width: 100%; box-sizing: border-box; margin-bottom: 12px; font-family: monospace; background: var(--bg); border: 1px solid var(--bdr); border-radius: 6px; padding: 10px; color: var(--t1); }
          .status-area { margin-top: 1rem; border-radius: 8px; padding: 0.85rem 1rem; font-size: 13px; line-height: 1.6; }
          .status-area.pending { background: rgba(245,158,11,0.1); color: #d97706; border: 1px solid rgba(245,158,11,0.2); }
          .status-area.success { background: rgba(16,185,129,0.1); color: #059669; border: 1px solid rgba(16,185,129,0.2); }
          .status-area.error { background: rgba(239,68,68,0.1); color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }
          .bk-tabs { display: flex; border-bottom: 1px solid var(--bdr); margin-bottom: 1rem; }
          .bk-tab { flex: 1; padding: 10px; font-size: 13px; cursor: pointer; text-align: center; color: var(--t2); border-bottom: 2px solid transparent; }
          .bk-tab.on { color: #E2136E; border-bottom-color: #E2136E; font-weight: 500; }
          .countdown { font-size: 11px; color: #d97706; margin-top: 8px; text-align: right; }

          /* ── Premium Plan Cards ── */
          .sub-plans-section { position: relative; }
          .plan-grid-premium {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-top: 8px;
          }
          .plan-card {
            position: relative;
            padding: 28px 22px 22px;
            border-radius: 16px;
            border: 1px solid var(--b2);
            background: linear-gradient(168deg, var(--s2) 0%, var(--s1) 100%);
            cursor: pointer;
            transition: all .25s cubic-bezier(.4,0,.2,1);
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          .plan-card::before {
            content: '';
            position: absolute; top: 0; left: 0; right: 0;
            height: 3px;
            background: var(--plan-accent, var(--ac));
            opacity: 0.5;
            transition: opacity .25s;
          }
          .plan-card:hover {
            border-color: var(--b3);
            transform: translateY(-4px);
            box-shadow: 0 16px 40px rgba(0,0,0,.3);
          }
          .plan-card:hover::before { opacity: 1; }
          .plan-card.selected {
            border-color: var(--plan-accent, var(--ac));
            box-shadow: 0 0 0 1px var(--plan-accent, var(--ac)), 0 12px 36px rgba(109,40,217,.2);
          }
          .plan-card.selected::before { opacity: 1; height: 4px; }
          .plan-card.popular-card::after {
            content: '★ Popular';
            position: absolute; top: 12px; right: 12px;
            font-size: 9px; font-weight: 800;
            padding: 3px 10px; border-radius: 99px;
            background: linear-gradient(135deg, var(--plan-accent, var(--ac)), #7c3aed);
            color: #fff;
            letter-spacing: 0.3px;
          }

          /* Plan card inner elements */
          .plan-card-name {
            font-weight: 800;
            font-size: 18px;
            margin-bottom: 4px;
            color: var(--plan-accent, var(--ac3));
          }
          .plan-card-price {
            font-size: 28px;
            font-weight: 800;
            color: var(--t1);
            margin-bottom: 2px;
            line-height: 1.2;
          }
          .plan-card-price .currency { font-size: 16px; font-weight: 600; vertical-align: super; margin-right: 2px; }
          .plan-card-price .period { font-size: 12px; font-weight: 400; color: var(--t3); }
          .plan-card-usd {
            font-size: 11px;
            color: var(--t3);
            margin-bottom: 16px;
          }
          .plan-card-credits {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font-size: 12px;
            font-weight: 700;
            color: var(--gr);
            background: rgba(16,185,129,0.08);
            border: 1px solid rgba(16,185,129,0.15);
            padding: 5px 12px;
            border-radius: 99px;
            margin-bottom: 16px;
            width: fit-content;
          }

          .plan-card-features {
            list-style: none;
            padding: 0;
            margin: 0 0 18px;
            flex: 1;
          }
          .plan-card-features li {
            font-size: 12px;
            color: var(--t2);
            padding: 4px 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .plan-card-features li::before {
            content: '✓';
            color: var(--gr);
            font-weight: 800;
            font-size: 11px;
            flex-shrink: 0;
          }

          /* Payment method indicators on plan cards */
          .plan-pay-methods {
            display: flex;
            gap: 6px;
            margin-bottom: 12px;
          }
          .ppm-badge {
            font-size: 10px;
            font-weight: 600;
            padding: 3px 8px;
            border-radius: 6px;
            background: var(--s3);
            color: var(--t2);
            border: 1px solid var(--b2);
          }
          .ppm-badge.bkash { color: #E2136E; border-color: rgba(226,19,110,0.2); background: rgba(226,19,110,0.06); }
          .ppm-badge.card { color: var(--ac3); border-color: rgba(109,40,217,0.2); background: rgba(109,40,217,0.06); }

          .plan-card-btn {
            width: 100%;
            padding: 11px;
            border-radius: 10px;
            border: none;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            transition: all .2s;
            color: #fff;
            background: linear-gradient(135deg, var(--plan-accent, var(--ac)), #7c3aed);
            box-shadow: 0 4px 14px rgba(109,40,217,0.25);
          }
          .plan-card-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 20px rgba(109,40,217,0.35);
          }
          .plan-card-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;
            background: var(--s3);
            color: var(--t3);
            box-shadow: none;
          }

          /* ── Payment Method Picker Modal ── */
          .pay-picker-overlay {
            position: fixed; inset: 0;
            background: rgba(0,0,0,.6);
            backdrop-filter: blur(6px);
            z-index: 50;
            display: flex; align-items: center; justify-content: center;
            padding: 20px;
            animation: fadeInPicker .2s ease-out;
          }
          @keyframes fadeInPicker {
            from { opacity: 0; } to { opacity: 1; }
          }
          .pay-picker {
            background: var(--s1);
            border: 1px solid var(--b2);
            border-radius: 16px;
            max-width: 380px; width: 100%;
            padding: 28px;
            box-shadow: 0 32px 80px rgba(0,0,0,.6);
            animation: slideUpPicker .25s ease-out;
          }
          @keyframes slideUpPicker {
            from { opacity: 0; transform: translateY(12px) scale(.97); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          .pay-picker-title {
            font-size: 16px;
            font-weight: 800;
            margin-bottom: 4px;
            color: var(--t1);
          }
          .pay-picker-sub {
            font-size: 12px;
            color: var(--t3);
            margin-bottom: 20px;
          }
          .pay-picker-options {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .pay-picker-btn {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 16px 18px;
            border-radius: 12px;
            border: 1px solid var(--b2);
            background: var(--s2);
            cursor: pointer;
            transition: all .2s;
            text-align: left;
          }
          .pay-picker-btn:hover {
            border-color: var(--b3);
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0,0,0,.2);
          }
          .pay-picker-btn .ppb-icon {
            width: 42px; height: 42px;
            border-radius: 10px;
            display: flex; align-items: center; justify-content: center;
            font-size: 20px;
            flex-shrink: 0;
          }
          .pay-picker-btn.bkash-btn .ppb-icon {
            background: rgba(226,19,110,0.1);
          }
          .pay-picker-btn.card-btn .ppb-icon {
            background: rgba(109,40,217,0.1);
          }
          .pay-picker-btn .ppb-info { flex: 1; }
          .pay-picker-btn .ppb-label { font-size: 14px; font-weight: 700; color: var(--t1); }
          .pay-picker-btn .ppb-desc { font-size: 11px; color: var(--t3); margin-top: 2px; }
          .pay-picker-btn .ppb-arrow { color: var(--t3); font-size: 16px; }

          .pay-picker-cancel {
            width: 100%;
            margin-top: 12px;
            padding: 10px;
            border-radius: 10px;
            border: 1px solid var(--b2);
            background: transparent;
            color: var(--t3);
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            transition: all .2s;
          }
          .pay-picker-cancel:hover { background: var(--s2); color: var(--t2); }

          /* ── Responsive ── */
          @media (max-width: 768px) {
            .plan-grid-premium { grid-template-columns: 1fr; gap: 12px; }
          }
      `}</style>

      <div className="main">
        <div className="mtbar">
          <div className="mtab on">Subscription</div>
          <div style={{ flex: 1 }} />
        </div>
        <div className="mbody">
          <div className="sub-page">
            <div className="sub-header">
              <div className="sub-title">Choose Your Plan</div>
              <div className="sub-desc">Unlock unlimited creative potential with aiField Studio</div>
            </div>

            <div className="billing-toggle">
              {['monthly', 'yearly'].map(b => (
                <button key={b} className={`pill ${billing === b ? 'on' : ''}`} onClick={() => setBilling(b)} style={{ padding: '7px 20px' }}>
                  {b === 'yearly' ? 'Yearly (15% off)' : 'Monthly'}
                </button>
              ))}
            </div>

            {/* ── Plan Cards ── */}
            <div className="plan-grid-premium">
              {displayPlans.map(p => {
                const pIdx = planOrder.indexOf(p.id);
                const isCurrent = p.id === plan;
                const isLowerOrEq = currentPlanIdx !== -1 && pIdx <= currentPlanIdx;
                const btnDisabled = isCurrent || isLowerOrEq;
                const finalBtnText = isCurrent ? "Current Plan" : (isLowerOrEq ? "Downgrade Unavailable" : "Subscribe");

                const bdtPrice = billing === 'yearly' ? Math.round(p.priceBDT * 12 * 0.85) : p.priceBDT;
                const baseUsdPrice = billing === 'yearly' ? Number((p.priceUSD * 12 * 0.85).toFixed(2)) : p.priceUSD;
                const usdPrice = isBD ? getBdCardUsdPrice(baseUsdPrice) : baseUsdPrice;
                const periodLabel = billing === 'yearly' ? 'yr' : 'mo';
                const totalCredits = billing === 'yearly' ? `${(p.credits * 12).toLocaleString()} credits/yr` : `${p.credits.toLocaleString()} credits/mo`;

                return (
                  <div
                    key={p.id}
                    className={`plan-card ${selPlan === p.id ? 'selected' : ''} ${p.popular ? 'popular-card' : ''}`}
                    style={{ '--plan-accent': p.color }}
                    onClick={() => setSelPlan(p.id)}
                  >
                    <div className="plan-card-name">{p.name}</div>
                    
                    {isBD ? (
                      <>
                        <div className="plan-card-price">
                          <span className="currency">৳</span>
                          {bdtPrice.toLocaleString()}
                          <span className="period"> /{periodLabel}</span>
                        </div>
                        <div className="plan-card-usd">
                          ${usdPrice} USD
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="plan-card-price">
                          <span className="currency">$</span>
                          {usdPrice}
                          <span className="period"> /{periodLabel}</span>
                        </div>
                        <div className="plan-card-usd">&nbsp;</div>
                      </>
                    )}

                    <div className="plan-card-credits">
                      ⚡ {totalCredits}
                    </div>

                    <ul className="plan-card-features">
                      {p.features.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>

                    {getPayMethodBadge(p.id)}

                    <button 
                      className="plan-card-btn"
                      style={!btnDisabled ? { background: `linear-gradient(135deg, ${p.color}, #7c3aed)` } : {}}
                      disabled={btnDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelPlan(p.id);
                        if (!btnDisabled) handlePlanAction(p.id);
                      }}
                    >
                      {subLoading && selPlan === p.id ? 'Processing…' : finalBtnText}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* ── Credit Packs ── */}
            <div className="buy-section mt-8">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                <div>
                  <div className="buy-title">Top Up Credits</div>
                  <div className="buy-desc">{isBD ? 'শুধু active subscriber দের জন্য।' : 'Available for active subscribers only.'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: 'var(--t3)', background: 'var(--s2)', border: '1px solid var(--b2)', borderRadius: 8, padding: '5px 10px' }}>
                  ⚡ Instant delivery after verification
                </div>
              </div>

              {currentPlanIdx === -1 ? (
                <div style={{ borderRadius: 14, border: '1px dashed var(--b2)', background: 'var(--s2)', padding: '36px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 }}>
                  <div style={{ fontSize: 36 }}>🔒</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--t1)' }}>Subscription Required</div>
                  <div style={{ fontSize: 13, color: 'var(--t3)', maxWidth: 340, lineHeight: 1.6 }}>
                    Credit top-ups are only available for active subscribers. Choose a plan above (Basic, Go, or Plus) to unlock this feature.
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4 }}>
                    {['Basic', 'Go', 'Plus'].map(n => (
                      <div key={n} style={{ padding: '4px 14px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: 'var(--s3)', color: 'var(--t3)', border: '1px solid var(--b2)' }}>{n}</div>
                    ))}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, padding: '6px 14px', borderRadius: 8, background: 'rgba(109,40,217,0.06)', border: '1px solid rgba(109,40,217,0.15)', color: 'var(--ac3)' }}>
                    ↑ Pick a plan above to get started
                  </div>
                </div>
              ) : (
              <>

              <div style={{
                marginBottom: 14,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid rgba(245,158,11,0.28)',
                background: 'rgba(245,158,11,0.08)',
                color: 'var(--t2)',
                fontSize: 12,
                lineHeight: 1.55,
              }}>
                {addonExpiryNotice}
              </div>

              <div className="pack-grid">
                {displayPacks.map((p, i) => (
                  <div key={i} className={`packc ${selPack === i ? 'on' : ''} ${p.popular ? 'pop' : ''}`} onClick={() => setSelPack(i)}>
                    <div className="packc-cr" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {p.credits} cr
                    </div>
                    {isBD ? (
                      <>
                        <div className="packc-pr">৳{p.priceBDT.toLocaleString()}</div>
                        <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 2 }}>${getBdCardUsdPrice(p.priceUSD)} USD</div>
                      </>
                    ) : (
                      <div className="packc-pr">${p.priceUSD}</div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 20 }}>
                <div className="slbl" style={{ marginBottom: 10 }}>Choose Payment Method</div>
                {isBD ? (
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => setPackPayMethod('bkash')}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                      transition: 'all .2s', textAlign: 'left',
                      border: packPayMethod === 'bkash' ? '2px solid #E2136E' : '1px solid var(--b2)',
                      background: packPayMethod === 'bkash' ? 'rgba(226,19,110,0.07)' : 'var(--s2)',
                    }}
                  >
                    <div style={{ width: 38, height: 38, borderRadius: 9, overflow: 'hidden', background: '#E2136E', flexShrink: 0 }}>
                      <img src="/bkash.png" alt="bKash" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: packPayMethod === 'bkash' ? '#E2136E' : 'var(--t1)' }}>bKash</div>
                      <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 1 }}>Send Money</div>
                    </div>
                    {packPayMethod === 'bkash' && <div style={{ marginLeft: 'auto', color: '#E2136E', fontWeight: 800 }}>✓</div>}
                  </button>

                  {/* Card payment temporarily removed */}
                </div>
                ) : (
                <div style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--s2)', color: 'var(--t2)', fontSize: 13 }}>
                  Card payments are temporarily unavailable.
                </div>
                )}
              </div>

              <div style={{
                marginTop: 14, padding: '10px 14px',
                background: 'var(--s2)', borderRadius: 8, border: '1px solid var(--b1)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 12, color: 'var(--t3)' }}>{selectedPack?.credits} credits</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--t1)' }}>
                  {isBD ? `৳${selectedPack?.priceBDT.toLocaleString()} · $${getBdCardUsdPrice(selectedPack?.priceUSD)}` : `$${selectedPack?.priceUSD} USD`}
                </span>
              </div>

                  <button className="gbtn" style={{ marginTop: 12, background: packPayMethod === 'card' || !isBD ? 'var(--s3)' : '#E2136E', color: packPayMethod === 'card' || !isBD ? 'var(--t3)' : '#fff' }}
                    onClick={() => {
                      if (!isBD || packPayMethod === 'card') {
                        handlePackCardCheckout();
                      } else {
                        openPayModal('pack', selPack);
                      }
                    }} disabled={buyLoading || packPayMethod === 'card' || !isBD}>
                    {!isBD || packPayMethod === 'card' ? 'Card payments temporarily disabled' : buyLoading ? 'Processing…' : `Buy ${selectedPack?.credits} Credits via bKash`}
                  </button>
              </>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ── Payment Method Picker (for Go/Plus plans) ── */}
      {showPlanPayPicker && (
        <div className="pay-picker-overlay" onClick={() => setShowPlanPayPicker(null)}>
          <div className="pay-picker" onClick={e => e.stopPropagation()}>
            <div className="pay-picker-title">Choose Payment Method</div>
            <div className="pay-picker-sub">
              Subscribe to {displayPlans.find(p => p.id === showPlanPayPicker)?.name} plan
              {isBD && ` — ৳${billing === 'yearly' ? Math.round((displayPlans.find(p => p.id === showPlanPayPicker)?.priceBDT || 0) * 12 * 0.85).toLocaleString() : (displayPlans.find(p => p.id === showPlanPayPicker)?.priceBDT || 0).toLocaleString()}`}
            </div>
            <div className="pay-picker-options">
              {isBD && PLAN_PAY_METHODS[showPlanPayPicker]?.includes('bkash') && (
                <button className="pay-picker-btn bkash-btn" onClick={() => {
                  setShowPlanPayPicker(null);
                  openPayModal('plan', showPlanPayPicker);
                }}>
                  <div className="ppb-icon" style={{ padding: 0, overflow: 'hidden' }}>
                    <img src="/bkash.png" alt="bKash" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div className="ppb-info">
                    <div className="ppb-label">bKash</div>
                    <div className="ppb-desc">Pay via bKash Send Money</div>
                  </div>
                  <div className="ppb-arrow">→</div>
                </button>
              )}
              {/* Card option temporarily removed */}
            </div>
            <button className="pay-picker-cancel" onClick={() => setShowPlanPayPicker(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── bKash Payment Modal ── */}
      {showPayModal && (
        <div className="mo" onClick={() => { if(countdown === 0 && (!bkashStatus || bkashStatus.type !== 'success')) setShowPayModal(false) }}>
          <div className="md" style={{ maxWidth: 460, padding: 0 }} onClick={e => e.stopPropagation()}>
             {payContext ? (
                 <div className="bk-card">
                    <div className="bk-header" style={{ position: 'relative' }}>
                      <div className="bk-logo">bK</div>
                      <div className="bk-header-text">
                        <h3>bKash payment</h3>
                        <p>
                          {payContext.type === 'plan'
                            ? `${PLANS.find(p => p.id === payContext.identifier)?.name || payContext.identifier} Plan — ৳${payContext.amount} BDT`
                            : `Credit Pack (${payContext.credits} cr) — ৳${payContext.amount} BDT`
                          }
                        </p>
                      </div>
                      <button 
                        onClick={() => { if(countdown === 0 && (!bkashStatus || bkashStatus.type !== 'success')) setShowPayModal(false) }}
                        style={{ position: 'absolute', right: 16, top: 22, background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', opacity: 0.8 }}
                      >
                        <Icons.X sz={20} />
                      </button>
                    </div>
                    <div className="bk-body">
                      <div className="bk-tabs">
                        <div className={`bk-tab ${bkashTab === 'pay' ? 'on' : ''}`} onClick={() => setBkashTab('pay')}>Make payment</div>
                        <div className={`bk-tab ${bkashTab === 'check' ? 'on' : ''}`} onClick={() => setBkashTab('check')}>Check status</div>
                      </div>

                      {bkashTab === 'pay' && (
                          <div>
                            {payContext.type === 'pack' && (
                              <div className="step-box" style={{ borderLeftColor: '#f59e0b', background: 'rgba(245,158,11,0.08)' }}>
                                {addonExpiryNotice}
                              </div>
                            )}
                            <div className="step-box">
                              <span style={{background: '#E2136E', color: 'white', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', marginRight: '6px'}}>Step 1</span>
                              Send money to our bKash number:<br/>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                                <div className="merchant-num" style={{ margin: 0 }}>01914873794</div>
                                <button className="gbtn" style={{ padding: '6px 12px', fontSize: '12px', background: 'rgba(226,19,110,0.1)', color: '#E2136E', border: '1px solid currentColor', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: 'none' }} onClick={() => { navigator.clipboard.writeText('01914873794'); toast.success('Number copied!'); }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg> Copy
                                </button>
                              </div>
                            </div>
                            <div className="step-box" style={{marginBottom: '1.5rem'}}>
                              <span style={{background: '#E2136E', color: 'white', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', marginRight: '6px'}}>Step 2</span>
                              Copy the <strong>TrxID</strong> from your bKash SMS and fill the form.
                            </div>

                            <label className="bk-label">Your bKash number</label>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                className="bk-input"
                                type="tel"
                                placeholder="e.g. 01712345678"
                                style={{ marginBottom: 0, flex: 1, letterSpacing: '1px' }}
                                value={senderNum}
                                onChange={e => setSenderNum(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                disabled={countdown > 0}
                                maxLength={11}
                              />
                            </div>
                            <div style={{ fontSize: 10, color: senderNum.length === 11 ? '#059669' : 'var(--t3)', marginTop: 4 }}>
                              {senderNum.length}/11 digits{senderNum.length > 0 && senderNum.length < 11 ? ` — need ${11 - senderNum.length} more` : senderNum.length === 11 ? ' ✓' : ''}
                            </div>

                            <div style={{display: 'flex', gap: '12px'}}>
                                <div style={{flex: 1}}>
                                    <label className="bk-label">Amount you pay (BDT)</label>
                                    <input className="bk-input" type="text" value={payContext.amount} disabled />
                                </div>
                                <div style={{flex: 1}}>
                                    <label className="bk-label">Credits you get</label>
                                    <input className="bk-input" style={{color: 'var(--t2)'}} type="text" value={payContext.credits + ' cr'} disabled />
                                </div>
                            </div>

                            <label className="bk-label">Transaction ID (TrxID from bKash SMS)</label>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                className="bk-input"
                                type="text"
                                placeholder="e.g. 8C4G2BLONQ or ABC1234XYZ"
                                style={{ textTransform: 'uppercase', marginBottom: 0, flex: 1, letterSpacing: '1px' }}
                                value={trxId}
                                onChange={e => setTrxId(e.target.value.toUpperCase())}
                                disabled={countdown > 0}
                              />
                              {trxId.length >= 6 && (
                                <button
                                  onClick={() => { navigator.clipboard.writeText(trxId); toast.success('TrxID copied!'); }}
                                  style={{
                                    padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(226,19,110,0.3)',
                                    background: 'rgba(226,19,110,0.08)', color: '#E2136E',
                                    fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                                    display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                                  }}
                                  title="Copy TrxID"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                  Copy
                                </button>
                              )}
                            </div>

                            {/* Terms agreement */}
                            <div style={{
                              marginTop: '20px',
                              padding: '12px 14px',
                              border: agreedToTerms ? '1px solid rgba(226,19,110,0.4)' : '1px solid var(--b2)',
                              borderRadius: '8px',
                              background: agreedToTerms ? 'rgba(226,19,110,0.05)' : 'var(--s2)',
                              transition: 'all 0.2s',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <input 
                                  type="checkbox" 
                                  checked={agreedToTerms} 
                                  onChange={(e) => setAgreedToTerms(e.target.checked)} 
                                  id="bkashTermCheck"
                                  style={{ cursor: 'pointer', marginTop: '2px', accentColor: '#E2136E', width: '15px', height: '15px', flexShrink: 0 }}
                                />
                                <label htmlFor="bkashTermCheck" style={{ cursor: 'pointer', userSelect: 'none', fontSize: '12.5px', color: 'var(--t2)', lineHeight: '1.5' }}>
                                  পেমেন্ট করার আগে আমি{' '}
                                  <a 
                                    href="/bkash-terms" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    style={{ color: '#E2136E', textDecoration: 'underline', fontWeight: 600 }}
                                  >
                                    Terms and Conditions
                                  </a>
                                  {' '}পড়েছি এবং সম্মত হচ্ছি।
                                </label>
                              </div>
                            </div>

                            {bkashStatus && (
                                <div className={`status-area ${bkashStatus.type}`} style={{marginTop: '12px'}} dangerouslySetInnerHTML={{__html: bkashStatus.msg}}></div>
                            )}

                            {countdown > 0 && <div className="countdown">Auto-checking... ({Math.floor(countdown/60)}:{(countdown%60).toString().padStart(2, '0')})</div>}
                            
                            {countdown === 0 && bkashStatus?.type === 'pending' && (
                                <button className="gbtn mt-4" style={{background: 'transparent', border:'1px solid var(--bdr)', color: 'var(--t2)'}} onClick={handleReportIssue}>
                                    Still pending? Submit a Report
                                </button>
                            )}

                            {countdown === 0 && (!bkashStatus || bkashStatus.type === 'error') && (
                                <button 
                                  className="gbtn mt-4" 
                                  onClick={submitBkash} 
                                  disabled={subLoading || !agreedToTerms} 
                                  style={{
                                    background: '#E2136E', 
                                    opacity: !agreedToTerms ? 0.45 : 1,
                                    cursor: !agreedToTerms ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                    {subLoading ? 'Submitting...' : 'Submit payment'}
                                </button>
                            )}

                          </div>
                      )}

                      {bkashTab === 'check' && (
                          <div>
                            <label className="bk-label">Enter your TrxID to check status</label>
                            <input className="bk-input" type="text" placeholder="e.g. ABC1234XYZ" style={{textTransform: 'uppercase'}} value={checkTrx} onChange={e=>setCheckTrx(e.target.value.toUpperCase())} />
                            <button className="gbtn mt-2" onClick={handleCheckStatus} style={{background: 'var(--s3)', color: 'var(--t1)'}}>Check status</button>

                            {checkStatusRes && (
                                <div className={`status-area ${checkStatusRes.type} mt-4`}>{checkStatusRes.msg}</div>
                            )}
                          </div>
                      )}
                    </div>
                 </div>
             ) : (
                 <div style={{padding: 28}}>
                    <div>Processing via Card flow...</div>
                 </div>
             )}
          </div>
        </div>
      )}
    </>
  );
}
